  <template>
  <div>
    <div class="row g-3">
      <!-- Source Category Dropdown -->
      <div class="col-12">
        <div class="dropdown w-100">
          <button
            class="btn d-flex align-items-center btn-outline-secondary dropdown-toggle w-100 text-start"
            type="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <label class="form-label small text-muted m-0 me-2">Category</label>
            <span class="ms-auto text-ellipsis">{{ currentCategoryLabel }}</span>
          </button>
          <ul class="dropdown-menu w-100">
            <li>
              <a
                class="dropdown-item"
                :class="{'active': !localCategory}"
                href="#"
                @click.prevent="handleCategoryChange(null)"
              >
                <em class="text-muted">No category selected</em>
              </a>
            </li>
            <li><hr class="dropdown-divider"></li>
            <template v-for="category in sourceCategories" :key="category.id">
              <li v-if="category.subcategories && category.subcategories.length > 0">
                <h6 class="dropdown-header">{{ category.name }}</h6>
              </li>
              <li v-for="subcategory in category.subcategories" :key="subcategory.id">
                <a
                  class="dropdown-item"
                  :class="{'active': localCategory == subcategory.id}"
                  href="#"
                  @click.prevent="handleCategoryChange(subcategory.id)"
                >
                  {{ subcategory.name }}
                </a>
              </li>
            </template>
          </ul>
        </div>
      </div>

      <!-- Source Location Dropdown -->
      <div class="col-12">
        <div class="dropdown w-100">
          <button
            class="btn d-flex align-items-center btn-outline-secondary dropdown-toggle w-100 text-start"
            type="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
           <label class="form-label small text-muted m-0 me-2">Location</label>
           <span class="ms-auto text-ellipsis">{{ currentLocationLabel }}</span>
          </button>
          <ul class="dropdown-menu w-100">
            <li>
              <a
                class="dropdown-item"
                :class="{'active': !localLocation}"
                href="#"
                @click.prevent="handleLocationChange(null)"
              >
                <em class="text-muted">No location selected</em>
              </a>
            </li>
            <li><hr class="dropdown-divider"></li>
            <template v-for="category in filteredLocations" :key="category.id">
              <li v-if="category.locations && category.locations.length > 0">
                <h6 class="dropdown-header">{{ category.name }}</h6>
              </li>
              <li v-for="location in category.locations" :key="location.id">
                <a
                  class="dropdown-item"
                  :class="{'active': localLocation == location.id}"
                  href="#"
                  @click.prevent="handleLocationChange(location.id, location.category_id)"
                >
                  {{ location.name }}<span v-if="location.city"> ({{ location.city }}<span v-if="location.state">, {{ location.state }}</span>)</span>
                </a>
              </li>
            </template>
            <li><hr class="dropdown-divider"></li>
            <li>
              <a
                class="dropdown-item text-primary fw-bold"
                href="#"
                @click.prevent="handleAddNewLocation"
              >
                <i class="bi bi-plus-circle me-2"></i>Add New Location...
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Inline Add Location Form -->
    <transition name="slide-fade">
      <div v-if="showAddLocationModal" ref="addLocationFormRef" class="add-location-form mt-3 p-3 border rounded bg-light">
        <div class="d-flex align-items-center mb-2">
          <i class="bi bi-plus-circle text-primary me-2"></i>
          <strong>Add New Location</strong>
        </div>
        <form @submit.prevent="saveNewLocation">
          <div class="mb-2">
            <label class="form-label small">Category <span class="text-danger">*</span></label>
            <source-categories-select v-model="newLocationForm.source_category_id" :enabled="true" class="form-select form-select-sm" />
          </div>
          <div class="mb-3">
            <label class="form-label small">Location Name <span class="text-danger">*</span></label>
            <input
              type="text"
              v-model="newLocationForm.name"
              class="form-control form-control-sm"
              placeholder="e.g., Goodwill Downtown"
              required
            />
          </div>
          <div class="d-flex gap-2">
            <button type="button" @click="cancelAddLocation" class="btn btn-sm btn-outline-secondary flex-fill" :disabled="savingNewLocation">
              Cancel
            </button>
            <button type="submit" class="btn btn-sm btn-primary flex-fill" :disabled="savingNewLocation">
              <span v-if="savingNewLocation" class="spinner-border spinner-border-sm me-1"></span>
              Add Location
            </button>
          </div>
        </form>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, computed, ref, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import SourceCategoriesSelect from '@/app/components/inventory/SourceCategories.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({ source_category_id: null, source_location_id: null })
  }
})

const emit = defineEmits(['update:modelValue'])
const store = useStore()

// Local state for category and location
const localCategory = ref(props.modelValue.source_category_id || null)
const localLocation = ref(props.modelValue.source_location_id || null)

// Add location modal state
const showAddLocationModal = ref(false)
const addLocationFormRef = ref(null)
const newLocationForm = ref({
  source_category_id: '',
  name: ''
})
const savingNewLocation = ref(false)

// Get source categories from store
const sourceCategories = computed(() => store.state.eshop.sourceCategories || [])

// Get source locations from store
const sourceLocations = computed(() => {
  const locations = store.state.eshop.sourceLocations || []
  return locations.filter(category => category.locations && category.locations.length > 0)
})

// Filter locations based on selected category
const filteredLocations = computed(() => {
  if (!localCategory.value) {
    return sourceLocations.value
  }

  // Find the category that contains this subcategory ID
  let targetCategoryId = null
  for (const category of sourceCategories.value) {
    const subcategory = category.subcategories?.find(sub => sub.id == localCategory.value)
    if (subcategory) {
      targetCategoryId = subcategory.id
      break
    }
  }

  if (!targetCategoryId) {
    return sourceLocations.value
  }

  // Filter locations by this category
  return sourceLocations.value.filter(category => {
    return category.locations.some(loc => loc.category_id == targetCategoryId)
  })
})

// Current category label
const currentCategoryLabel = computed(() => {
  if (!localCategory.value) {
    return 'Select a category...'
  }

  for (const category of sourceCategories.value) {
    const subcategory = category.subcategories?.find(sub => sub.id == localCategory.value)
    if (subcategory) {
      return subcategory.name
    }
  }

  return 'Unknown category'
})

// Current location label
const currentLocationLabel = computed(() => {
  if (!localLocation.value) {
    return 'Select a location...'
  }

  for (const category of sourceLocations.value) {
    const location = category.locations?.find(loc => loc.id == localLocation.value)
    if (location) {
      let label = location.name
      if (location.city) {
        label += ` (${location.city}`
        if (location.state) {
          label += `, ${location.state}`
        }
        label += ')'
      }
      return label
    }
  }

  return 'Unknown location'
})

// Handle category change
function handleCategoryChange(categoryId) {
  localCategory.value = categoryId

  if (!categoryId) {
    // Category cleared — always clear location too since it's a child
    localLocation.value = null
  } else if (localLocation.value) {
    // Category changed — check if current location is still valid
    const locationStillValid = filteredLocations.value.some(category =>
      category.locations.some(loc => loc.id == localLocation.value)
    )
    if (!locationStillValid) {
      localLocation.value = null
    }
  }

  emitUpdate()
}

// Handle location change
function handleLocationChange(locationId, categoryId = null) {
  localLocation.value = locationId

  // Auto-populate category if location is selected and category isn't set
  if (locationId && categoryId) {
    localCategory.value = categoryId
  }

  emitUpdate()
}

// Emit update to parent
function emitUpdate() {
  emit('update:modelValue', {
    source_category_id: localCategory.value,
    source_location_id: localLocation.value
  })
}

// Watch for external changes to modelValue
watch(() => props.modelValue, (newVal) => {
  localCategory.value = newVal.source_category_id || null
  localLocation.value = newVal.source_location_id || null
}, { deep: true })

// Handle add new location
function handleAddNewLocation() {
  showAddLocationModal.value = true
  // Pre-select current category if one is selected
  if (localCategory.value) {
    newLocationForm.value.source_category_id = localCategory.value
  }
}

// Scroll inline form into view when it appears
watch(showAddLocationModal, async (newVal) => {
  if (newVal) {
    await nextTick()
    addLocationFormRef.value?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }
})

// Save new location from mini-modal
async function saveNewLocation() {
  if (!newLocationForm.value.name || !newLocationForm.value.source_category_id) {
    alert('Please provide both category and location name')
    return
  }

  savingNewLocation.value = true
  try {
    const result = await store.dispatch('eshop/saveSourceLocation', {
      location: newLocationForm.value
    })

    // Auto-select the newly created location
    if (result && result.id) {
      localLocation.value = result.id
      localCategory.value = newLocationForm.value.source_category_id
      emitUpdate()
    }

    // Close modal and reset form
    showAddLocationModal.value = false
    newLocationForm.value = {
      source_category_id: '',
      name: ''
    }
  } catch (error) {
    alert(error.response?.data?.error || error.message || 'Failed to save location')
  } finally {
    savingNewLocation.value = false
  }
}

function cancelAddLocation() {
  showAddLocationModal.value = false
  newLocationForm.value = {
    source_category_id: '',
    name: ''
  }
}
</script>

<style lang="scss" scoped>
.add-location-form {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

// Slide-fade transition
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1.0, 0.5, 0.8, 1.0);
}

.slide-fade-enter-from {
  transform: translateY(-10px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(-5px);
  opacity: 0;
}

.dropdown-menu {
  max-height: 300px;
  overflow-y: auto;
}
</style>
